import { Children, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { isSupportedBrandingLogo } from './partnerDashboardPresentation';
import type {
  Client,
  ClientListResponse,
  Organization,
  OrganizationListResponse,
  OrganizationMember,
  OrganizationMemberListResponse,
  PartnerDashboard,
  ReportBranding,
} from '../types';

export default function PartnerDashboardPage() {
  const { t, i18n } = useTranslation();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [organizationId, setOrganizationId] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [clientId, setClientId] = useState('');
  const [assigneeUserId, setAssigneeUserId] = useState('');
  const [status, setStatus] = useState('');
  const [dueBefore, setDueBefore] = useState('');
  const [dashboard, setDashboard] = useState<PartnerDashboard | null>(null);
  const [error, setError] = useState('');
  const [branding, setBranding] = useState<ReportBranding | null>(null);
  const [newOrganizationName, setNewOrganizationName] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get<OrganizationListResponse>('/api/organizations')
      .then(response => {
        setOrganizations(response.data.organizations);
        setOrganizationId(response.data.organizations[0]?.id ?? '');
      })
      .catch(() => setError(t('partner.errors.workspaces')));
  }, [t]);

  useEffect(() => {
    if (!organizationId) return;
    setClientId('');
    setAssigneeUserId('');
    Promise.all([
      api.get<ClientListResponse>(
        `/api/organizations/${organizationId}/clients`
      ),
      api.get<OrganizationMemberListResponse>(
        `/api/organizations/${organizationId}/members`
      ),
      api.get<ReportBranding>(`/api/organizations/${organizationId}/branding`),
    ])
      .then(([clientResponse, memberResponse, brandingResponse]) => {
        setClients(clientResponse.data.clients);
        setMembers(memberResponse.data.members);
        setBranding(brandingResponse.data);
      })
      .catch(() => setError(t('partner.errors.workspaceDetails')));
  }, [organizationId, t]);

  useEffect(() => {
    if (!organizationId) return;
    const query = new URLSearchParams();
    if (clientId) query.set('clientId', clientId);
    if (assigneeUserId) query.set('assigneeUserId', assigneeUserId);
    if (status) query.set('status', status);
    if (dueBefore) query.set('dueBefore', dueBefore);
    api
      .get<PartnerDashboard>(
        `/api/organizations/${organizationId}/dashboard?${query}`
      )
      .then(response => {
        setDashboard(response.data);
        setError('');
      })
      .catch(() => setError(t('partner.errors.dashboard')));
  }, [organizationId, clientId, assigneeUserId, status, dueBefore, t]);

  const cards = useMemo(
    () =>
      dashboard
        ? [
            [t('partner.metrics.activeProjects'), dashboard.projects.length],
            [t('partner.metrics.needAttention'), dashboard.attentionProjects],
            [t('partner.metrics.overdueTasks'), dashboard.overdueTasks.length],
            [t('partner.metrics.activeClients'), dashboard.activeClients],
          ]
        : [],
    [dashboard, t]
  );
  const currentOrganization = organizations.find(
    item => item.id === organizationId
  );
  const canConfigure =
    Boolean(currentOrganization) &&
    currentOrganization?.currentRole !== 'member';
  const saveBranding = async () => {
    if (!branding) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await api.put<ReportBranding>(
        `/api/organizations/${organizationId}/branding`,
        branding
      );
      setBranding(response.data);
      setError('');
      setNotice(t('partner.branding.saved'));
    } catch {
      setNotice('');
      setError(t('partner.errors.branding'));
    } finally {
      setSaving(false);
    }
  };

  const uploadBrandingLogo = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const logo = event.target.files?.[0];
    if (!logo || !organizationId) return;
    if (!isSupportedBrandingLogo(logo)) {
      event.target.value = '';
      setNotice('');
      setError('Logo must be a PNG, JPEG, or SVG no larger than 512 KB.');
      return;
    }
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const formData = new FormData();
      formData.append('logo', logo);
      const response = await api.post<ReportBranding>(
        `/api/organizations/${organizationId}/branding/logo`,
        formData
      );
      setBranding(response.data);
      setNotice(t('partner.branding.saved'));
    } catch {
      setError(t('partner.errors.branding'));
    } finally {
      setSaving(false);
      event.target.value = '';
    }
  };

  const createOrganization = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newOrganizationName.trim()) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await api.post<Organization>('/api/organizations', {
        name: newOrganizationName.trim(),
      });
      setOrganizations(current => [...current, response.data]);
      setOrganizationId(response.data.id);
      setNewOrganizationName('');
      setNotice(t('partner.workspaceCreated', { name: response.data.name }));
    } catch {
      setError(t('partner.errors.createWorkspace'));
    } finally {
      setSaving(false);
    }
  };

  const createClient = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!organizationId || !newClientName.trim()) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await api.post<Client>(
        `/api/organizations/${organizationId}/clients`,
        { name: newClientName.trim() }
      );
      setClients(current => [...current, response.data]);
      setClientId(response.data.id);
      setNewClientName('');
      setNotice(t('partner.clientCreated', { name: response.data.name }));
    } catch {
      setError(t('partner.errors.createClient'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="page-intro">
        <div className="page-eyebrow">{t('partner.eyebrow')}</div>
        <h1 style={{ color: 'var(--text-primary)', margin: '8px 0' }}>
          {t('partner.title')}
        </h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          {t('partner.subtitle')}
        </p>
      </div>
      <section style={panelStyle}>
        <h2 style={{ ...sectionHeadingStyle, marginTop: 0 }}>
          {t('partner.manageTitle')}
        </h2>
        <p style={{ color: 'var(--text-secondary)', marginTop: -4 }}>
          {t('partner.manageHint')}
        </p>
        <div className="workspace-management-grid">
          <form onSubmit={createOrganization} className="inline-create-form">
            <label style={labelStyle}>
              {t('partner.newWorkspace')}
              <input
                value={newOrganizationName}
                onChange={event => setNewOrganizationName(event.target.value)}
                placeholder={t('partner.workspacePlaceholder')}
                style={inputStyle}
              />
            </label>
            <button
              className="button button-secondary"
              disabled={saving || !newOrganizationName.trim()}
            >
              {t('partner.createWorkspace')}
            </button>
          </form>
          <form onSubmit={createClient} className="inline-create-form">
            <label style={labelStyle}>
              {t('partner.newClient')}
              <input
                value={newClientName}
                onChange={event => setNewClientName(event.target.value)}
                placeholder={t('partner.clientPlaceholder')}
                style={inputStyle}
                disabled={!canConfigure}
              />
            </label>
            <button
              className="button button-secondary"
              disabled={saving || !canConfigure || !newClientName.trim()}
            >
              {t('partner.createClient')}
            </button>
          </form>
        </div>
      </section>
      <section style={panelStyle}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: 12,
          }}
        >
          <Filter
            label={t('workspace.workspace')}
            value={organizationId}
            onChange={setOrganizationId}
            options={organizations.map(item => [item.id, item.name])}
          />
          <Filter
            label={t('workspace.client')}
            value={clientId}
            onChange={setClientId}
            options={[
              ['', t('partner.filters.allClients')],
              ...clients.map(item => [item.id, item.name]),
            ]}
          />
          <Filter
            label={t('partner.filters.assignee')}
            value={assigneeUserId}
            onChange={setAssigneeUserId}
            options={[
              ['', t('partner.filters.allAssignees')],
              ...members.map(item => [item.userId, item.name || item.email]),
            ]}
          />
          <Filter
            label={t('partner.filters.status')}
            value={status}
            onChange={setStatus}
            options={[
              ['', t('partner.filters.allStatuses')],
              ['review_requested', t('partner.filters.reviewRequested')],
              ['changes_requested', t('partner.filters.changesRequested')],
              ['open', t('partner.filters.openTasks')],
              ['in_progress', t('partner.filters.inProgressTasks')],
            ]}
          />
          <label style={labelStyle}>
            {t('partner.filters.dueBefore')}
            <input
              type="date"
              value={dueBefore}
              onChange={event => setDueBefore(event.target.value)}
              style={inputStyle}
            />
          </label>
        </div>
      </section>
      {branding && (
        <details style={{ ...panelStyle, marginTop: 16 }}>
          <summary
            style={{
              color: 'var(--text-primary)',
              cursor: 'pointer',
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            {t('partner.branding.title')}
          </summary>
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
                alignItems: 'end',
              }}
            >
              <Filter
                label={t('partner.branding.attribution')}
                value={branding.mode}
                onChange={value =>
                  setBranding({
                    ...branding,
                    mode: value as ReportBranding['mode'],
                  })
                }
                options={[
                  ['ce_scan', 'CE-Scan'],
                  ['co_branded', t('partner.branding.coBranded')],
                  ['white_label', t('partner.branding.whiteLabel')],
                ]}
              />
              <label style={labelStyle}>
                {t('partner.branding.organizationName')}
                <input
                  value={branding.organizationName}
                  disabled={!canConfigure}
                  onChange={event =>
                    setBranding({
                      ...branding,
                      organizationName: event.target.value,
                    })
                  }
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                Logo
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  disabled={!canConfigure}
                  onChange={event => void uploadBrandingLogo(event)}
                  style={inputStyle}
                />
                <small style={{ color: 'var(--text-muted)' }}>
                  PNG, JPEG, or SVG up to 512 KB
                </small>
              </label>
              <label style={labelStyle}>
                {t('partner.branding.footer')}
                <input
                  value={branding.footerText}
                  disabled={!canConfigure}
                  onChange={event =>
                    setBranding({ ...branding, footerText: event.target.value })
                  }
                  style={inputStyle}
                />
              </label>
              <label style={labelStyle}>
                {t('partner.branding.accent')}
                <input
                  type="color"
                  value={branding.accentColor}
                  disabled={!canConfigure}
                  onChange={event =>
                    setBranding({
                      ...branding,
                      accentColor: event.target.value.toUpperCase(),
                    })
                  }
                  style={inputStyle}
                />
              </label>
              <button
                className="button button-primary"
                disabled={
                  saving || !canConfigure || !branding.organizationName.trim()
                }
                onClick={() => void saveBranding()}
              >
                {t('partner.branding.save')}
              </button>
            </div>
            {branding.logoUrl && (
              <img
                src={branding.logoUrl}
                alt="Organization logo"
                style={{
                  display: 'block',
                  marginTop: 12,
                  maxHeight: 48,
                  maxWidth: 180,
                  objectFit: 'contain',
                }}
              />
            )}
            <p
              style={{
                color: 'var(--text-muted)',
                fontSize: 12,
                marginBottom: 0,
              }}
            >
              {t('partner.branding.hint')}
            </p>
          </div>
        </details>
      )}
      {notice && (
        <p role="status" className="status-message status-message-success">
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" className="status-message status-message-error">
          {error}
        </p>
      )}
      {dashboard && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 14,
              margin: '18px 0',
            }}
          >
            {cards.map(([label, value]) => (
              <div key={label} style={panelStyle}>
                <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                  {label}
                </div>
                <strong
                  style={{ color: 'var(--accent-primary)', fontSize: 28 }}
                >
                  {value}
                </strong>
              </div>
            ))}
          </div>
          <DashboardSection
            title={t('partner.sections.attention')}
            emptyLabel={t('partner.empty.attention')}
          >
            {dashboard.attentionGroups.map(group => (
              <div
                key={group.clientId || 'unassigned'}
                style={{ display: 'grid', gap: 8, marginBottom: 8 }}
              >
                <strong style={{ color: 'var(--text-primary)', fontSize: 14 }}>
                  {group.clientName || t('workspace.noClient')}
                </strong>
                {group.projects.map(project => (
                  <Link
                    key={project.id}
                    to={`/projects/${project.id}`}
                    style={rowStyle}
                  >
                    <span>
                      <strong>{project.name}</strong>
                      <small>{project.reviewStatus.replace(/_/g, ' ')}</small>
                    </span>
                    <span>
                      {t('partner.projectCounts', {
                        gaps: project.unresolvedGapCount,
                        tasks: project.openTaskCount,
                      })}
                    </span>
                  </Link>
                ))}
              </div>
            ))}
          </DashboardSection>
          <DashboardSection
            title={t('partner.sections.overdue')}
            emptyLabel={t('partner.empty.overdue')}
          >
            {dashboard.overdueTasks.map(task => (
              <Link
                key={task.id}
                to={`/projects/${task.projectId}`}
                style={rowStyle}
              >
                <span>
                  <strong>{task.title}</strong>
                  <small>
                    {task.projectName}
                    {task.clientName ? ` · ${task.clientName}` : ''}
                  </small>
                </span>
                <span>
                  {task.dueDate
                    ? new Date(task.dueDate).toLocaleDateString(i18n.language)
                    : ''}
                </span>
              </Link>
            ))}
          </DashboardSection>
          <DashboardSection
            title={t('partner.sections.exports')}
            emptyLabel={t('partner.empty.exports')}
          >
            {dashboard.recentExports.map((item, index) => (
              <Link
                key={`${item.projectId}-${item.createdAt}-${index}`}
                to={`/projects/${item.projectId}`}
                style={rowStyle}
              >
                <span>
                  <strong>{item.projectName}</strong>
                  <small>
                    {t('partner.exportLabel', {
                      format: t(
                        `results.audit.formats.${item.format.toLowerCase()}`,
                        {
                          defaultValue: item.format
                            .toUpperCase()
                            .split('_')
                            .join(' '),
                        }
                      ),
                    })}
                  </small>
                </span>
                <span>
                  {new Date(item.createdAt).toLocaleDateString(i18n.language)}
                </span>
              </Link>
            ))}
          </DashboardSection>
        </>
      )}
    </div>
  );
}

function Filter({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[][];
}) {
  return (
    <label style={labelStyle}>
      {label}
      <select
        value={value}
        onChange={event => onChange(event.target.value)}
        style={inputStyle}
      >
        {options.map(([id, name]) => (
          <option key={id || 'all'} value={id}>
            {name}
          </option>
        ))}
      </select>
    </label>
  );
}

function DashboardSection({
  title,
  emptyLabel,
  children,
}: {
  title: string;
  emptyLabel: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ ...panelStyle, marginBottom: 16 }}>
      <h2 style={{ color: 'var(--text-primary)', fontSize: 18 }}>{title}</h2>
      <div style={{ display: 'grid', gap: 8 }}>
        {Children.count(children) > 0 ? (
          children
        ) : (
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>{emptyLabel}</p>
        )}
      </div>
    </section>
  );
}

const panelStyle: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: 18,
};
const sectionHeadingStyle: React.CSSProperties = {
  color: 'var(--text-primary)',
  fontSize: 18,
};
const labelStyle: React.CSSProperties = {
  color: 'var(--text-secondary)',
  fontSize: 12,
};
const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 6,
  padding: 9,
  background: 'var(--bg-tertiary)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
};
const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 16,
  color: 'var(--text-secondary)',
  textDecoration: 'none',
  padding: 12,
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
};
